/*
 * Copyright (c) 2020. American Express Company
 */

import _ from 'lodash';
import { LocalDate } from '@js-joda/core';
import React, { Component, ReactComponentElement } from 'react';

// models and services
import { CardModel } from 'src/types/card.model';
import CardService from '../../services/card.service';
import LiteralsService from '../../services/literals/LiteralsService';

// components
import { FieldControl, Label } from '@americanexpress/dls-react';
import Anchor from '../atomic/atoms/anchor/Anchor';
import BWCurrencyInput from 'src/bw/bw-currency-input/BW-Currency-Input';
import GoalDrawer from './GoalDrawer';
import InfoCallout from '../callout/InfoCallout';
import SavingsGoal from 'src/bw/bw-drawer/common/SavingsGoal';
import Typography from '../typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// icons and styles
import { ReactComponent as BabyIcon } from 'src/bw/goal/baby.svg';
import { ReactComponent as JessyNeutral } from '../mentors/jessy-neutral.svg';

export default class GrowFamilyDrawer extends Component<any> {
    private saveTimer: number = 0;

    public override render(): ReactComponentElement<any> {
        const drawerProps: any = this.props.drawerProps;

        let jessy: JSX.Element = null;
        if (_.get(this.props, 'mentor.state') === 'updateGoal') {
            if (_.get(this.props, 'mentor.goal') === 'take a vacation') {
                drawerProps.card.startDate = drawerProps.card.endDate = LocalDate.now().plusMonths(2);

                jessy = (
                    <div className="jessy after24" key="jessy">
                        <div className="avatar">
                            <JessyNeutral />
                        </div>
                        <Typography variant={TypographyVariants.CAPTION} className="caption">
                            Now you have a chance to change the details about your goal.{' '}
                            <b>Let's update the cost of this vacation to $15,000.</b>
                        </Typography>
                    </div>
                );
            }
        }

        return (
            <GoalDrawer
                drawerProps={drawerProps}
                handleBack={this.props.handleBack}
                handleUpdateCard={this.props.handleUpdateCard}
                handleChangeCard={this.props.handleChangeCard}
                handleDeleteCard={this.props.handleDeleteCard}
                showLoader={this.props.showLoader}
                hideLoader={this.props.hideLoader}
                icon={<BabyIcon />}
                introContent={LiteralsService.getLiteral('ExpenseGoalDrawer.baby.subtitle')}
                cashFlow={this.props.user.cashFlow}
                handleChangeCardDate={this.props.handleChangeCardDate.bind(this)}
                startDateLabel={LiteralsService.getLiteral('ExpenseGoalDrawer.baby.startDateLabel')}
                hasEndDate
                endDateLabel="Your child is independent date"
                bottomButtonUpdate={this.handleUpdateCard.bind(this)}
            >
                <FieldControl>
                    <Label htmlFor="amount">Expected expense</Label>
                    <BWCurrencyInput
                        className="amount"
                        defaultValue={0}
                        id="amount"
                        status=""
                        value={-drawerProps.card.attributes.totalAmount}
                        showFractions={false}
                        onChange={(value: any) => {
                            this.handleSaveCost(value);
                            if (this.saveTimer) {
                                clearTimeout(this.saveTimer);
                            }
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                            this.saveTimer = window.setTimeout(async () => {
                                this.handleUpdateSavingsGoal(value);
                            }, 1000);
                        }}
                        fullWidth
                    />
                </FieldControl>

                <InfoCallout noMargin>
                    {LiteralsService.getLiteral('ExpenseGoalDrawer.baby.tip1')}
                    <Anchor
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://www.usda.gov/media/blog/2017/01/13/cost-raising-child"
                    >
                        US Department of Agriculture
                    </Anchor>
                    {LiteralsService.getLiteral('ExpenseGoalDrawer.baby.tip2')}
                </InfoCallout>

                {jessy}

                <SavingsGoal
                    drawerProps={drawerProps}
                    user={this.props.user}
                    setDrawer={this.props.setDrawer}
                    handleOpenSignup={() => {
                        console.warn('Deprecated: handleOpenSignup (Grow Family)');
                        this.props.handleOpenSignup();
                    }}
                    handleCreateLinkedSavingsCard={this.props.handleCreateLinkedSavingsCard}
                    handleChangeCardAttribute={this.props.handleChangeCardAttribute}
                    handleUpdateCard={this.props.handleUpdateCard}
                    handleDeleteCard={this.props.handleDeleteCard}
                />
            </GoalDrawer>
        );
    }

    private handleUpdateCard(): void {
        if (this.props.drawerProps.card.attributes.purchaseName) {
            this.props.drawerProps.card.nickname = this.props.drawerProps.card.nickname
                ? this.props.drawerProps.card.nickname
                : `Save for ${this.props.drawerProps.card.attributes.purchaseName}`;
        }
        this.props.handleUpdateCard();
    }

    private handleSaveCost(value: number): void {
        this.props.handleChangeCardAttribute(this.props.drawerProps.card, -Number(value || 0), 'totalAmount');
    }

    private handleUpdateSavingsGoal(value: number): void {
        if (CardService.hasSavingsGoal(this.props)) {
            const updatedCard: CardModel = CardService.getSavingsGoal(this.props);
            _.set(updatedCard, 'attributes.targetBalance', Number(value || 0));
            this.props.handleUpdateCard(updatedCard);
        }
    }
}
